import { Box, IconButton } from '@mui/material';
import { Edit } from '@tempo-io/sui-icons';
import React from 'react';
import { DEFAULT_PLUGINS } from './plugins';
import { RichTextDisplay } from './RichTextDisplay';
import { RichTextEditor } from './RichTextEditor';
import type { RichTextEditableProps } from './types';
import { useRichTextController } from './useRichTextController';

export const RichTextEditable = React.memo<RichTextEditableProps>(
  ({
    value,
    defaultValue,
    editing,
    defaultEditing,
    onSave,
    onCancel,
    onChange,
    plugins = DEFAULT_PLUGINS,
    placeholder,
    autoFocus = false,
    sx,
    showEditButton = true,
  }) => {
    const controller = useRichTextController({
      value,
      defaultValue,
      editing,
      defaultEditing,
      onSave,
      onCancel,
      onChange,
    });

    const handleDisplayClick = () => {
      if (!editing) {
        controller.startEditing();
      }
    };

    if (controller.isEditing) {
      return (
        <RichTextEditor
          value={controller.value}
          onChange={controller.handleChange}
          onSave={() => controller.handleSave()}
          onCancel={controller.handleCancel}
          plugins={plugins}
          placeholder={placeholder}
          autoFocus={autoFocus}
          sx={sx}
        />
      );
    }

    if (!controller.value.trim()) {
      return null;
    }

    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 1,
          position: 'relative',
          '&:hover .rich-text-edit-button': {
            opacity: showEditButton ? 1 : 0,
          },
          ...sx,
        }}
      >
        <RichTextDisplay value={controller.value} onClick={handleDisplayClick} sx={{ flex: 1 }} />

        {showEditButton && (
          <IconButton
            size="small"
            onClick={handleDisplayClick}
            className="rich-text-edit-button"
            sx={{
              opacity: 0,
              transition: 'opacity 0.2s',
              alignSelf: 'flex-start',
            }}
            data-testid="rich-text-edit-button"
          >
            <Edit />
          </IconButton>
        )}
      </Box>
    );
  },
);
